"use client";
import React, {
  forwardRef,
  ComponentProps,
  ReactNode,
  ButtonHTMLAttributes,
} from "react";
import { MouseEventHandler } from "react";
import styles from "./Button.module.scss";
import { useRouter } from "next/compat/router";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import { customSanitize } from "@/utils/customSanitize.ts";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import { CustomLink } from "../CustomLink";
import { FadeInFromBottom, FadeInWithPulse } from "../Animation/Animation";

export type ButtonTypes = "primary" | "secondary";

interface ButtonProps extends ComponentProps<"a"> {
  type: ButtonTypes;
  children: ReactNode;
  animation?: "fade" | "pulse" | "none";
  /** Renders normal button component with on Click Event Handler*/
  onClick?: MouseEventHandler<HTMLElement>;
  href: string;
  className?: string;
  target: string;
  /**
   * Creates a link with no prefetching abilities... which Next Links per default have
   */
  disablePrefetch?: boolean;
  /**
   * Creates a link which triggers prefetching only on hover... Next Links prefetch per default as soon as link is visible
   */
  onlyPrefetchOnHover?: boolean;
  isDownload?: boolean;
  isBack?: boolean;
}

export const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      type,
      children,
      animation,
      onClick,
      href = "_self",
      className,
      target = "_self",
      disablePrefetch = false,
      onlyPrefetchOnHover = false,
      isDownload = false,
      isBack = false,
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const { asPath } = useRouter() ?? {};
    const { getIcon } = useGlobalContext();
    const btnClassName = removeExtraSpacesFromClassNames(
      `btn ${`is-${type}`} ${styles.btn} ${styles[`is-` + type]} ${className || ""} ${isBack ? styles["is-back"] : ""}`,
    );

    if (!href && !onClick) {
      throw new Error(`No href provided at "Button.tsx" at ${asPath}`);
    }
    // else if (!target && !onClick) {
    //   throw new Error(`No target provided at "Button.tsx" at ${asPath}`);
    // }

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      onClick?.(e);
    };

    const innerButtonJSX =
      !!href && !onClick ? (
        <CustomLink
          href={href}
          target={target}
          className={btnClassName}
          disablePrefetch={disablePrefetch}
          onlyPrefetchOnHover={onlyPrefetchOnHover}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...props}
        >
          {children}
          {type === "primary" && !isDownload && (
            <ReactSVG
              src={getIcon("arrowRight")}
              wrapper={"svg"}
              width={24}
              height={24}
              aria-hidden={true}
              beforeInjection={(svg) => {
                svg.setAttribute("alt", `Arrow right Icon`);
                svg.setAttribute("aria-label", `Arrow right Icon`);
              }}
            />
          )}
          {type === "primary" && isDownload && (
            <ReactSVG
              src={getIcon("download")}
              wrapper={"svg"}
              width={16}
              height={16}
              beforeInjection={(svg) => {
                svg.setAttribute("alt", "Download Icon");
                svg.setAttribute("aria-label", "Download Icon");
              }}
            />
          )}
        </CustomLink>
      ) : (
        onClick && (
          <button
            onClick={handleClick}
            className={`${btnClassName} button-reset`}
            aria-label={props["aria-label"] ?? ""}
            ref={ref as React.Ref<HTMLButtonElement>}
            {...(props as ComponentProps<"button">)}
          >
            {children}
            {type === "primary" && !isDownload && (
              <ReactSVG
                src={getIcon("altArrowRight")}
                wrapper={"svg"}
                width={24}
                height={24}
                aria-hidden={true}
                beforeInjection={(svg) => {
                  svg.setAttribute("alt", `Arrow right Icon`);
                  svg.setAttribute("aria-label", `Arrow right Icon`);
                }}
              />
            )}
            {type === "primary" && isDownload && (
              <ReactSVG
                src={getIcon("download")}
                wrapper={"svg"}
                width={16}
                height={16}
                beforeInjection={(svg) => {
                  svg.setAttribute("alt", `Download Icon`);
                  svg.setAttribute("aria-label", `Download Icon`);
                }}
              />
            )}
          </button>
        )
      );

    if (!innerButtonJSX) {
      return null;
    }

    return animation === "fade" ? (
      <FadeInFromBottom>{innerButtonJSX}</FadeInFromBottom>
    ) : animation === "pulse" ? (
      <FadeInWithPulse>{innerButtonJSX}</FadeInWithPulse>
    ) : (
      innerButtonJSX
    );
  },
);
Button.displayName = "Button";

interface SimpleButtonProp extends ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
  extraClassName?: string;
  btnType?: ButtonTypes;
}

export const SimpleButton = forwardRef<HTMLButtonElement, SimpleButtonProp>(
  ({ text, extraClassName, btnType = "primary", ...props }, ref) => {
    const { getIcon } = useGlobalContext();

    const btnClassName = removeExtraSpacesFromClassNames(
      `btn ${styles.btn} ${styles[`is-${btnType}`]} ${extraClassName || ""}`,
    );

    return (
      <button className={btnClassName} ref={ref} {...props}>
        <span dangerouslySetInnerHTML={{ __html: customSanitize(text) }}></span>
        {btnType === "secondary" && (
          <ReactSVG
            src={getIcon("arrowRight")}
            wrapper={"svg"}
            width={24}
            height={24}
            aria-hidden={true}
            beforeInjection={(svg) => {
              svg.setAttribute("alt", `Arrow right Icon`);
              svg.setAttribute("aria-label", `Arrow right Icon`);
            }}
          />
        )}
      </button>
    );
  },
);

SimpleButton.displayName = "SimpleButton";
